import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  TextField,
  Grid,
  Autocomplete,
  IconButton,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Article } from '../types';
import { Client } from '../types/client';
import { Demandeur } from '../types/demandeur';
import { Service } from '../types/service';
import { Projet } from '../types/projet';
import { StockExitNew, StockExitNewUpdate, StockExitItemCreate } from '../types/stockExitNew';
import {
  stockExitsNewAPI,
  stockEntriesAPI,
  getCurrentStock,
} from '../services/api';
import { extractErrorMessage } from '../utils/errorHandler';
import ConfirmationDialog, { ConfirmationField } from './ConfirmationDialog';
import PriceInput, { parsePriceValue } from './PriceInput';

interface StockExitNewEditProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  stockExit: StockExitNew | null;
  articles: Article[];
  clients: Client[];
  demandeurs: Demandeur[];
  services: Service[];
  projets: Projet[];
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
}

/** Above this margin a line gets a warning (not a block): usually a typo in the price. */
const MARGE_ALERTE = 300;
const margeElevee = (m: number | string | null) =>
  (typeof m === 'string' ? parseFloat(m.replace(',', '.')) : m ?? 0) > MARGE_ALERTE;

interface ArticleLineItem extends Omit<StockExitItemCreate, 'quantity' | 'prix_entree' | 'marge'> {
  availableStock?: number;
  loadingStock?: boolean;
  stockWarning?: string;
  article?: Article;
  quantity: number | null | string;
  prix_entree: number | null | string;
  marge: number | null | string;
  prix_sortie: number | null | string;
  originalQuantity?: number;
}

const StockExitNewEdit: React.FC<StockExitNewEditProps> = ({
  open,
  onClose,
  onSuccess,
  stockExit,
  articles,
  clients,
  demandeurs,
  services,
  projets,
  showError,
  showSuccess,
}) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    bon_number: '',
    is_paid: false,
    client_id: null as number | null,
    client_code: '',
    demandeur_id: null as number | null,
    service_id: null as number | null,
    projet_id: null as number | null,
    advance: null as number | null | string,
  });

  const [articleLines, setArticleLines] = useState<ArticleLineItem[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form when stockExit changes
  useEffect(() => {
    if (stockExit && open) {
      const client = clients.find((c) => c.id === stockExit.client_id);
      setFormData({
        date: stockExit.date || new Date().toISOString().split('T')[0],
        bon_number: stockExit.bon_number || '',
        is_paid: stockExit.is_paid || false,
        client_id: stockExit.client_id,
        client_code: client?.code || stockExit.client_code || '',
        demandeur_id: stockExit.demandeur_id,
        service_id: stockExit.service_id,
        projet_id: stockExit.projet_id,
        advance: stockExit.advance ?? null,
      });

      // Initialize article lines from stockExit items
      const lines: ArticleLineItem[] = stockExit.items.map((item) => ({
        article_id: item.article_id,
        article: articles.find((a) => a.id === item.article_id),
        quantity: Number(item.quantity),
        prix_entree: Number(item.prix_entree),
        marge: Number(item.marge),
        prix_sortie: Number(item.prix_sortie),
        originalQuantity: Number(item.quantity),
      }));

      setArticleLines(lines.length > 0 ? lines : [{
        article_id: 0,
        quantity: null,
        prix_entree: null,
        marge: null,
        prix_sortie: null,
      }]);

      // Fetch stock info for all items
      lines.forEach(async (line, index) => {
        if (line.article_id) {
          try {
            const stockInfo = await getCurrentStock(line.article_id);
            const currentStock = (stockInfo.stock_final || 0) + (line.originalQuantity || 0);
            const article = articles.find((a) => a.id === line.article_id);

            setArticleLines((prev) => {
              const newLines = [...prev];
              if (newLines[index]) {
                newLines[index] = {
                  ...newLines[index],
                  availableStock: currentStock,
                  stockWarning: currentStock <= 0
                    ? `Stock épuisé`
                    : currentStock <= (article?.seuil_alerte || 0)
                      ? `Stock faible (Disponible: ${currentStock})`
                      : undefined,
                };
              }
              return newLines;
            });
          } catch (error) {
            console.error('Error fetching stock:', error);
          }
        }
      });
    }
  }, [stockExit, open, articles, clients]);

  const formatNumber = (value: any, decimals: number = 2): string => {
    return Number(value || 0).toFixed(decimals);
  };

  const handleAddArticleLine = () => {
    setArticleLines([
      ...articleLines,
      {
        article_id: 0,
        quantity: null,
        prix_entree: null,
        marge: null,
        prix_sortie: null,
      },
    ]);
  };

  const handleRemoveArticleLine = (index: number) => {
    if (articleLines.length > 1) {
      setArticleLines(articleLines.filter((_, i) => i !== index));
    }
  };

  const handleArticleChange = async (index: number, article: Article | null) => {
    const newLines = [...articleLines];

    if (article) {
      newLines[index].loadingStock = true;
      newLines[index].stockWarning = undefined;
      setArticleLines([...newLines]);

      let stockWarning: string | undefined = undefined;
      let availableStock: number | undefined = undefined;

      try {
        const stockInfo = await getCurrentStock(article.id);
        const currentStock = stockInfo.stock_final || 0;
        availableStock = currentStock;

        if (currentStock <= 0) {
          stockWarning = `Stock épuisé (Stock: ${currentStock})`;
        } else if (currentStock <= (article.seuil_alerte || 0)) {
          stockWarning = `Stock faible (Disponible: ${currentStock})`;
        }
      } catch (error) {
        console.error('Error fetching current stock:', error);
        stockWarning = 'Impossible de vérifier le stock';
      }

      let maxUnitPrice = 0;
      try {
        const response = await stockEntriesAPI.getArticleMaxPrice(article.id);
        maxUnitPrice = response.max_price;
      } catch (error) {
        console.error('Error fetching article max price:', error);
      }

      const updatedLines = [...articleLines];
      const margeValue = updatedLines[index].marge;
      const currentMarge = typeof margeValue === 'string'
        ? parseFloat(margeValue.replace(',', '.')) || 0
        : (margeValue ?? 0);
      updatedLines[index] = {
        ...updatedLines[index],
        article_id: article.id,
        article: article,
        prix_entree: Number(maxUnitPrice),
        prix_sortie: Math.round(Number(maxUnitPrice) * (1 + (currentMarge as number) / 100) * 100) / 100,
        availableStock: availableStock,
        stockWarning: stockWarning,
        loadingStock: false,
      };

      setArticleLines(updatedLines);
    } else {
      const updatedLines = [...articleLines];
      updatedLines[index] = {
        article_id: 0,
        quantity: null,
        prix_entree: null,
        marge: null,
        prix_sortie: null,
      };
      setArticleLines(updatedLines);
    }
  };

  const handleArticleFieldChange = (
    index: number,
    field: keyof ArticleLineItem,
    value: any
  ) => {
    const newLines = [...articleLines];

    newLines[index] = {
      ...newLines[index],
      [field]: value,
    };

    const prixEntreeValue = newLines[index].prix_entree;
    const prixEntree = typeof prixEntreeValue === 'string'
      ? parseFloat(prixEntreeValue.replace(',', '.')) || 0
      : (prixEntreeValue ?? 0);

    const quantityValue = newLines[index].quantity;
    const quantity = typeof quantityValue === 'string'
      ? parseFloat(quantityValue.replace(',', '.')) || 0
      : (quantityValue ?? 0);

    // Check if quantity exceeds available stock
    if (field === 'quantity' && newLines[index].availableStock !== undefined) {
      const availableStock = newLines[index].availableStock!;
      if (quantity > availableStock) {
        newLines[index].stockWarning = `Quantité (${quantity}) > Stock (${availableStock})`;
      } else if (quantity <= availableStock && newLines[index].stockWarning?.includes('>')) {
        const article = newLines[index].article;
        if (article && availableStock <= (article.seuil_alerte || 0)) {
          newLines[index].stockWarning = `Stock faible (Disponible: ${availableStock})`;
        } else if (availableStock > 0) {
          newLines[index].stockWarning = undefined;
        }
      }
    }

    // Auto-calculate prix_sortie when marge or prix_entree changes
    if (field === 'marge' || field === 'prix_entree') {
      const margeValue = newLines[index].marge;
      const marge = typeof margeValue === 'string'
        ? parseFloat(margeValue.replace(',', '.')) || 0
        : (margeValue ?? 0);

      // Calculate PRIX S = PRIX E × (1 + marge/100)
      const prixSortie = Math.round((prixEntree as number) * (1 + (marge as number) / 100) * 100) / 100;
      newLines[index].prix_sortie = prixSortie;
    }

    // When user manually edits prix_sortie, recalculate marge based on prix_entree
    if (field === 'prix_sortie' && prixEntree > 0) {
      const prixSortieValue = newLines[index].prix_sortie;
      const prixSortie = typeof prixSortieValue === 'string'
        ? parseFloat(prixSortieValue.replace(',', '.')) || 0
        : (prixSortieValue ?? 0);

      // Calculate marge = ((prix_sortie / prix_entree) - 1) * 100
      const calculatedMarge = Math.round(((prixSortie / prixEntree) - 1) * 100 * 100) / 100;
      newLines[index].marge = calculatedMarge;
    }

    setArticleLines(newLines);
  };

  const handleClientChange = (clientId: number | null) => {
    const selectedClient = clients.find((client) => client.id === clientId);
    const isMAF = selectedClient?.code === 'CLI-001';

    setFormData({
      ...formData,
      client_id: clientId,
      client_code: selectedClient ? selectedClient.code : '',
      demandeur_id: isMAF ? formData.demandeur_id : null,
      service_id: isMAF ? formData.service_id : null,
      projet_id: isMAF ? formData.projet_id : null,
    });
  };

  const calculateGrandTotal = (): number => {
    return articleLines.reduce((total, line) => {
      const quantity = typeof line.quantity === 'string'
        ? parseFloat(line.quantity.replace(',', '.')) || 0
        : (line.quantity ?? 0);
      const prixSortie = typeof line.prix_sortie === 'string'
        ? parseFloat(line.prix_sortie.replace(',', '.')) || 0
        : (line.prix_sortie ?? 0);
      return total + quantity * prixSortie;
    }, 0);
  };

  const handleSubmitClick = () => {
    if (!formData.bon_number || !formData.bon_number.trim()) {
      showError('Veuillez saisir le numéro de bon');
      return;
    }

    if (!formData.client_id) {
      showError('Veuillez sélectionner un client');
      return;
    }

    const isMAF = formData.client_code === 'CLI-001';

    if (isMAF && !formData.demandeur_id) {
      showError('Veuillez sélectionner un demandeur');
      return;
    }

    if (isMAF && !formData.service_id) {
      showError('Veuillez sélectionner un service');
      return;
    }

    if (isMAF && !formData.projet_id) {
      showError('Veuillez sélectionner un projet');
      return;
    }

    for (let i = 0; i < articleLines.length; i++) {
      const line = articleLines[i];

      if (!line.article_id || line.article_id === 0) {
        showError(`Veuillez sélectionner un article pour la ligne ${i + 1}`);
        return;
      }

      const quantity = typeof line.quantity === 'string'
        ? parseFloat(line.quantity.replace(',', '.'))
        : line.quantity;

      if (quantity === null || isNaN(quantity as number) || (quantity as number) <= 0) {
        showError(`La quantité doit être supérieure à 0 pour la ligne ${i + 1}`);
        return;
      }
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    if (!stockExit?.id) return;

    setIsSubmitting(true);

    try {
      const updateData: StockExitNewUpdate = {
        date: formData.date || new Date().toISOString().split('T')[0],
        bon_number: formData.bon_number,
        is_paid: formData.is_paid,
        advance: parsePriceValue(formData.advance),
        client_id: formData.client_id,
        client_code: formData.client_code || null,
        demandeur_id: formData.demandeur_id,
        service_id: formData.service_id,
        projet_id: formData.projet_id,
        items: articleLines.map((line) => {
          const quantity = typeof line.quantity === 'string'
            ? parseFloat(line.quantity.replace(',', '.'))
            : (line.quantity ?? 0);
          const prixEntree = typeof line.prix_entree === 'string'
            ? parseFloat(line.prix_entree.replace(',', '.'))
            : (line.prix_entree ?? 0);
          const marge = typeof line.marge === 'string'
            ? parseFloat(line.marge.replace(',', '.'))
            : (line.marge ?? 0);
          return {
            article_id: line.article_id,
            quantity: Math.round(quantity * 100) / 100,
            prix_entree: Math.round(prixEntree * 100) / 100,
            marge: Math.round(marge * 100) / 100,
          };
        }),
      };

      await stockExitsNewAPI.update(stockExit.id, updateData);
      setShowConfirmDialog(false);
      showSuccess('Sortie de stock modifiée avec succès');
      onSuccess();
      handleClose();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getConfirmationFields = (): ConfirmationField[] => {
    const fields: ConfirmationField[] = [];

    fields.push({ label: 'Date', value: formData.date });
    fields.push({ label: 'BON N°', value: formData.bon_number });

    if (formData.client_id) {
      const client = clients.find((c) => c.id === formData.client_id);
      if (client) {
        fields.push({ label: 'Client', value: `${client.code} - ${client.name}` });
      }
    }

    if (formData.demandeur_id) {
      const demandeur = demandeurs.find((d) => d.id === formData.demandeur_id);
      if (demandeur) {
        fields.push({ label: 'Demandeur', value: `${demandeur.prenom} ${demandeur.nom}` });
      }
    }

    if (formData.service_id) {
      const service = services.find((s) => s.id === formData.service_id);
      if (service) {
        fields.push({ label: 'Service', value: service.name });
      }
    }

    if (formData.projet_id) {
      const projet = projets.find((p) => p.id === formData.projet_id);
      if (projet) {
        fields.push({ label: 'Projet', value: `${projet.code} - ${projet.nom}` });
      }
    }

    fields.push({ label: '---', value: '---' });
    fields.push({ label: 'Articles', value: `${articleLines.length}`, bold: true });

    articleLines.forEach((line, index) => {
      const article = articles.find((a) => a.id === line.article_id);
      if (article) {
        const quantity = typeof line.quantity === 'string'
          ? parseFloat(line.quantity.replace(',', '.')) || 0
          : (line.quantity ?? 0);
        const prixSortie = typeof line.prix_sortie === 'string'
          ? parseFloat(line.prix_sortie.replace(',', '.')) || 0
          : (line.prix_sortie ?? 0);

        fields.push({
          label: `Article ${index + 1}`,
          value: `${article.reference} - ${article.designation}`,
        });
        fields.push({ label: `  Quantité`, value: `${quantity}` });
        fields.push({ label: `  Prix Sortie`, value: `${formatNumber(prixSortie)} MAD` });
        fields.push({ label: `  Total`, value: `${formatNumber(quantity * prixSortie)} MAD` });
      }
    });

    fields.push({ label: '---', value: '---' });
    fields.push({
      label: 'TOTAL GÉNÉRAL',
      value: `${formatNumber(calculateGrandTotal())} MAD`,
      bold: true,
    });

    return fields;
  };

  const handleClose = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      bon_number: '',
      is_paid: false,
      client_id: null,
      client_code: '',
      demandeur_id: null,
      service_id: null,
      projet_id: null,
      advance: null,
    });
    setArticleLines([]);
    setShowConfirmDialog(false);
    setIsSubmitting(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>Modifier la sortie de stock</DialogTitle>
        <DialogContent>
          {/* Date and BON Number */}
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="BON N°"
                  value={formData.bon_number}
                  fullWidth
                  required
                  disabled
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: '#000000',
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_paid}
                      onChange={(e) => setFormData({ ...formData, is_paid: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Payé"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <PriceInput
                  label="Avance (acompte)"
                  value={formData.advance}
                  onChange={(v) => setFormData({ ...formData, advance: v })}
                  variant="outlined"
                  size="medium"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Article Lines Section */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Articles</Typography>
              <IconButton
                color="primary"
                size="small"
                onClick={handleAddArticleLine}
                sx={{ border: '2px solid', borderColor: 'primary.main' }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            {articleLines.map((line, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      options={articles}
                      getOptionLabel={(option) => `${option.reference} - ${option.designation}`}
                      value={articles.find((a) => a.id === line.article_id) || null}
                      onChange={(_, value) => handleArticleChange(index, value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Article"
                          placeholder="Sélectionner article"
                          size="small"
                          required
                        />
                      )}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      label="Quantité"
                      type="text"
                      value={line.quantity ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'quantity', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'quantity', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'quantity', numValue);
                          } else {
                            handleArticleFieldChange(index, 'quantity', null);
                          }
                        }
                      }}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      label="Prix MAX"
                      type="text"
                      value={line.prix_entree ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'prix_entree', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'prix_entree', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'prix_entree', numValue);
                          } else {
                            handleArticleFieldChange(index, 'prix_entree', null);
                          }
                        }
                      }}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      label="Marge %"
                      type="text"
                      value={line.marge ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'marge', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'marge', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'marge', numValue);
                          } else {
                            handleArticleFieldChange(index, 'marge', null);
                          }
                        }
                      }}
                      helperText={margeElevee(line.marge) ? 'Marge élevée : vérifiez le prix' : undefined}
                      FormHelperTextProps={{ sx: { color: 'warning.main', mx: 0 } }}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      label="Prix S"
                      type="text"
                      value={line.prix_sortie ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'prix_sortie', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'prix_sortie', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'prix_sortie', numValue);
                          } else {
                            handleArticleFieldChange(index, 'prix_sortie', null);
                          }
                        }
                      }}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm="auto" md="auto" sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleRemoveArticleLine(index)}
                      disabled={articleLines.length === 1}
                      sx={{
                        border: '1px solid',
                        borderColor: articleLines.length === 1 ? 'grey.300' : 'error.main',
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>

                  {line.stockWarning && (
                    <Grid item xs={12}>
                      <Alert
                        severity={line.availableStock !== undefined && line.availableStock <= 0 ? 'error' : 'warning'}
                        icon={<WarningIcon />}
                        sx={{ py: 0 }}
                      >
                        {line.stockWarning}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Client, Demandeur, Service, Projet Fields */}
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={formData.client_code === 'CLI-001' ? 6 : 12}>
                <Autocomplete
                  options={clients}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  value={clients.find((c) => c.id === formData.client_id) || null}
                  onChange={(_, value) => handleClientChange(value?.id || null)}
                  renderInput={(params) => <TextField {...params} label="Client" required />}
                />
              </Grid>

              {formData.client_code === 'CLI-001' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={demandeurs}
                      getOptionLabel={(option) => `${option.prenom} ${option.nom}`}
                      value={demandeurs.find((d) => d.id === formData.demandeur_id) || null}
                      onChange={(_, value) => setFormData({ ...formData, demandeur_id: value?.id || null })}
                      renderInput={(params) => <TextField {...params} label="Demandeur" required />}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={services}
                      getOptionLabel={(option) => option.name}
                      value={services.find((s) => s.id === formData.service_id) || null}
                      onChange={(_, value) => setFormData({ ...formData, service_id: value?.id || null })}
                      renderInput={(params) => <TextField {...params} label="Service" required />}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={projets}
                      getOptionLabel={(option) => `${option.code} - ${option.nom}`}
                      value={projets.find((p) => p.id === formData.projet_id) || null}
                      onChange={(_, value) => setFormData({ ...formData, projet_id: value?.id || null })}
                      renderInput={(params) => <TextField {...params} label="Projet" required />}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSubmitClick} variant="contained">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={showConfirmDialog}
        title="Confirmer la modification"
        message="Veuillez vérifier les informations avant de confirmer la modification."
        fields={getConfirmationFields()}
        movementType="exit"
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowConfirmDialog(false)}
        confirmText="Confirmer la modification"
        cancelText="Annuler"
        loading={isSubmitting}
      />
    </>
  );
};

export default StockExitNewEdit;
